import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  Card,
  Segmented,
  Spin,
  Row,
  Col,
  Typography,
  Progress,
  Space,
  Input,
  Select,
  DatePicker,
  Tooltip,
  Table,
} from "antd";
import {
  TrophyOutlined,
  WarningOutlined,
  BarChartOutlined,
  SearchOutlined,
  TeamOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR  = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

/* ─── Colour helpers ─── */
const getZone = (rate) => {
  if (rate >= 90) return { stroke: "#10b981", light: "#d1fae5", text: "#065f46", label: "Excellent" };
  if (rate >= 75) return { stroke: "#3b82f6", light: "#dbeafe", text: "#1e3a8a", label: "Good" };
  if (rate >= 60) return { stroke: "#f59e0b", light: "#fef3c7", text: "#78350f", label: "Average" };
  return           { stroke: "#ef4444", light: "#fee2e2", text: "#7f1d1d", label: "Critical" };
};

const medalLabel = (rank) => {
  if (rank === 1) return { icon: "🥇", bg: "#fef9c3", color: "#854d0e" };
  if (rank === 2) return { icon: "🥈", bg: "#f1f5f9", color: "#334155" };
  if (rank === 3) return { icon: "🥉", bg: "#fff7ed", color: "#7c2d12" };
  return { icon: `#${rank}`, bg: "#f8fafc", color: "#64748b" };
};

/* ================================================ */
export default function DepartmentAttendanceBenchmark() {
  const { authData, backUrl } = useContext(AuthContext) || {};

  const [loading, setLoading]           = useState(true);
  const [deptData, setDeptData]         = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [orgId, setOrgId]               = useState("ALL");
  const [dateMode, setDateMode]         = useState("month");
  const [month, setMonth]               = useState(CURRENT_MONTH);
  const [year, setYear]                 = useState(CURRENT_YEAR);
  const [customDate, setCustomDate]     = useState(dayjs());
  const [search, setSearch]             = useState("");
  const [sortOrder, setSortOrder]       = useState("desc");

  /* fetch organisations */
  useEffect(() => {
    if (!authData?.access) return;
    const apiBase = backUrl || "";
    axios
      .get(`${apiBase}/api/emp/organisations/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      })
      .then((r) => setOrganisations(r.data.results || r.data || []))
      .catch(() => {});
  }, [backUrl, authData?.access]);

  /* fetch benchmark */
  useEffect(() => {
    if (!authData?.access) return;
    setLoading(true);
    const apiBase = backUrl || "";
    const params = {};
    if (orgId && orgId !== "ALL") params.organisation_id = orgId;
    if (dateMode === "custom" && customDate) {
      params.date = customDate.format("YYYY-MM-DD");
    } else {
      params.month = month;
      params.year  = year;
    }
    axios
      .get(`${apiBase}/api/reports/department-attendance-benchmark/`, {
        params,
        headers: { Authorization: `Bearer ${authData?.access}` },
      })
      .then((r) => setDeptData(r.data?.departments || []))
      .catch(() => setDeptData([]))
      .finally(() => setLoading(false));
  }, [orgId, month, year, dateMode, customDate, backUrl, authData?.access]);

  /* derived */
  const byRate    = [...deptData].sort((a, b) => b.rate - a.rate);
  const top       = byRate[0];
  const bottom    = byRate[byRate.length - 1];
  const avg       = deptData.length
    ? Math.round((deptData.reduce((s, d) => s + d.rate, 0) / deptData.length) * 10) / 10
    : 0;
  const totalStaff = deptData.reduce((s, d) => s + (d.total_staff || 0), 0);

  const filtered = deptData.filter((d) =>
    (d.name || "").toLowerCase().includes(search.toLowerCase())
  );
  const tableData = [...filtered]
    .sort((a, b) => sortOrder === "desc" ? b.rate - a.rate : a.rate - b.rate)
    .map((d, i) => ({ ...d, rank: i + 1, key: d.id || i }));

  const periodLabel = dateMode === "custom"
    ? customDate.format("DD MMM YYYY")
    : `${MONTHS[month - 1]} ${year}`;

  /* ─── Table columns ─── */
  const columns = [
    {
      title: "Rank",
      dataIndex: "rank",
      width: 60,
      align: "center",
      render: (rank) => {
        const m = medalLabel(rank);
        return (
          <span style={{
            background: m.bg, color: m.color,
            borderRadius: 8, padding: "2px 9px",
            fontSize: 13, fontWeight: 800, whiteSpace: "nowrap",
          }}>
            {m.icon}
          </span>
        );
      },
    },
    {
      title: "Department",
      dataIndex: "name",
      render: (name, rec) => (
        <div>
          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>{name}</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
            <TeamOutlined style={{ marginRight: 3 }} />
            {rec.total_staff} active staff
          </div>
        </div>
      ),
    },
    {
      title: "Attendance Compliance Rate",
      dataIndex: "rate",
      render: (rate) => {
        const z = getZone(rate);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ flex: 1 }}>
              <Progress
                percent={rate}
                strokeColor={z.stroke}
                trailColor="#e2e8f0"
                showInfo={false}
                strokeWidth={10}
                style={{ margin: 0 }}
              />
            </div>
            <span style={{
              fontWeight: 900, fontSize: 16, color: z.stroke,
              minWidth: 48, textAlign: "right",
            }}>
              {rate}%
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 18,
        background: "#fff",
        boxShadow: "0 2px 20px rgba(15,23,42,0.07)",
        border: "1px solid #e2e8f0",
      }}
      bodyStyle={{ padding: "20px 22px" }}
    >
      {/* ── HEADER + FILTERS ── */}
      <Row justify="space-between" align="middle" gutter={[0, 10]} style={{ marginBottom: 18 }}>
        <Col xs={24} md={10}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: "linear-gradient(135deg,#3b82f6 0%,#6366f1 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <BarChartOutlined style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>
                Department Attendance Benchmark
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                Ranked compliance report · {periodLabel}
              </div>
            </div>
          </div>
        </Col>

        <Col xs={24} md={14}>
          <Space wrap size={6} style={{ justifyContent: "flex-end", width: "100%" }}>
            <Select value={orgId} onChange={setOrgId} style={{ width: 155 }} size="small">
              <Option value="ALL">All Organisations</Option>
              {organisations.map((o) => (
                <Option key={o.id} value={o.id}>{o.name}</Option>
              ))}
            </Select>

            <Segmented
              size="small"
              value={dateMode}
              onChange={setDateMode}
              options={[
                { label: "Monthly", value: "month" },
                { label: "Custom Date", value: "custom" },
              ]}
            />

            {dateMode === "month" ? (
              <>
                <Select value={month} onChange={setMonth} style={{ width: 108 }} size="small">
                  {MONTHS.map((m, i) => (
                    <Option key={i + 1} value={i + 1}>{m}</Option>
                  ))}
                </Select>
                <Select value={year} onChange={setYear} style={{ width: 80 }} size="small">
                  {YEARS.map((y) => (
                    <Option key={y} value={y}>{y}</Option>
                  ))}
                </Select>
              </>
            ) : (
              <DatePicker
                allowClear={false}
                value={customDate}
                onChange={(v) => setCustomDate(v || dayjs())}
                style={{ width: 130 }}
                size="small"
              />
            )}

            <Input
              size="small"
              placeholder="Search department…"
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ width: 145, borderRadius: 7 }}
            />

            <Segmented
              size="small"
              value={sortOrder}
              onChange={setSortOrder}
              options={[
                { label: <span><ArrowDownOutlined /> Highest</span>, value: "desc" },
                { label: <span><ArrowUpOutlined /> Lowest</span>,  value: "asc"  },
              ]}
            />
          </Space>
        </Col>
      </Row>

      {/* ── KPI SUMMARY STRIP ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {/* Top Dept */}
        <Col xs={24} sm={6}>
          <div style={{
            background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
            border: "1px solid #bbf7d0", borderRadius: 14,
            padding: "12px 14px", minHeight: 72,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#15803d", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
              🥇 Top Department
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#14532d", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {top?.name || "—"}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#10b981", lineHeight: 1 }}>
              {top?.rate ?? 0}%
            </div>
          </div>
        </Col>

        {/* Bottom Dept */}
        <Col xs={24} sm={6}>
          <div style={{
            background: "linear-gradient(135deg,#fff1f2,#fee2e2)",
            border: "1px solid #fecaca", borderRadius: 14,
            padding: "12px 14px", minHeight: 72,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#b91c1c", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
              ⚠️ Needs Attention
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#7f1d1d", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {bottom?.name || "—"}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#ef4444", lineHeight: 1 }}>
              {bottom?.rate ?? 0}%
            </div>
          </div>
        </Col>

        {/* Company Average */}
        <Col xs={24} sm={6}>
          <div style={{
            background: "linear-gradient(135deg,#eff6ff,#dbeafe)",
            border: "1px solid #bfdbfe", borderRadius: 14,
            padding: "12px 14px", minHeight: 72,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
              📊 Company Average
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#3b82f6", lineHeight: 1, marginBottom: 2 }}>
              {avg}%
            </div>
            <div style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 600 }}>
              {deptData.length} departments
            </div>
          </div>
        </Col>

        {/* Total Workforce */}
        <Col xs={24} sm={6}>
          <div style={{
            background: "linear-gradient(135deg,#faf5ff,#ede9fe)",
            border: "1px solid #ddd6fe", borderRadius: 14,
            padding: "12px 14px", minHeight: 72,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
              👥 Total Workforce
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#6d28d9", lineHeight: 1, marginBottom: 2 }}>
              {totalStaff}
            </div>
            <div style={{ fontSize: 11, color: "#7c3aed", fontWeight: 600 }}>
              Active Employees
            </div>
          </div>
        </Col>
      </Row>

      {/* ── COLOUR LEGEND ── */}
      <div style={{ display: "flex", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
        <Text style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, marginRight: 4 }}>Zones:</Text>
        {[
          { label: "Excellent", color: "#10b981", range: "≥ 90%" },
          { label: "Good",      color: "#3b82f6", range: "75–89%" },
          { label: "Average",   color: "#f59e0b", range: "60–74%" },
          { label: "Critical",  color: "#ef4444", range: "< 60%" },
        ].map((z) => (
          <span key={z.label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#475569" }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: z.color, display: "inline-block" }} />
            <strong style={{ color: z.color }}>{z.label}</strong> {z.range}
          </span>
        ))}
      </div>

      {/* ── LEADERBOARD TABLE ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 14, color: "#64748b", fontWeight: 600, fontSize: 13 }}>
            Calculating attendance benchmarks…
          </div>
        </div>
      ) : (
        <Table
          dataSource={tableData}
          columns={columns}
          pagination={tableData.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
          size="small"
          rowKey="key"
          scroll={{ x: 680 }}
          locale={{
            emptyText: (
              <div style={{ padding: "40px 0", textAlign: "center", color: "#94a3b8" }}>
                <BarChartOutlined style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                <div style={{ fontWeight: 600 }}>No data found for the selected filters</div>
              </div>
            ),
          }}
          rowClassName={(_, idx) => idx % 2 === 0 ? "" : ""}
          onRow={(rec) => ({
            style: {
              background: rec.rank <= 3 && sortOrder === "desc"
                ? rec.rank === 1 ? "#fffef0"
                : rec.rank === 2 ? "#f8fafc"
                : "#fff8f5"
                : "#fff",
              transition: "background 0.15s",
            },
          })}
          style={{
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
          }}
        />
      )}
    </Card>
  );
}
